/* City Border: quantity-triggered add-on. No frontend price overrides.
 * Must only be enabled after the server discount and welcome exclusions pass checkout QA.
 */
(function () {
  'use strict';
  if (window.CityBorderStore) return;
  const node = document.getElementById('CbStoreSettings');
  if (!node) return;
  let cfg;
  try { cfg = JSON.parse(node.textContent); } catch (_) { return; }
  const mains = new Set((cfg.qualifyingIds || []).map(String));
  const extras = new Set((cfg.addonIds || []).map(String));
  const base = (cfg.root || '/').replace(/\/?$/, '/');
  const normal = Number(cfg.normalPrice), offer = Number(cfg.offerPrice);
  const reduction = normal - offer;
  let busy = false, uncertain = false, lastCart = null, revision = 0, timer;
  const heldControls = new Set();
  const format = (cents, currency = 'EUR') => new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(cents / 100);
  const cartItems = cart => Array.isArray(cart?.items) ? cart.items : [];
  function evaluateCart(cart) {
    const items = cartItems(cart);
    const mainQuantity = items.reduce((n, i) => n + (mains.has(String(i.product_id)) ? Math.max(0, Number(i.quantity) || 0) : 0), 0);
    const lines = items.filter(i => extras.has(String(i.product_id)) && Number(i.quantity) > 0);
    let promotionLines = 0, applied = 0, invalid = false;
    for (const line of lines) {
      let allocated = 0;
      for (const d of line.line_level_discount_allocations || []) {
        const amount = Number(d.amount) || 0;
        if (d.discount_application?.title === cfg.discountTitle) allocated += amount;
        else if (amount > 0) invalid = true;
      }
      if (allocated > 0) { promotionLines++; applied += allocated; }
      const qty = Number(line.quantity);
      const original = Number(line.original_line_price ?? Number(line.original_price ?? line.price) * qty);
      const final = Number(line.final_line_price ?? line.line_price);
      if (!Number.isFinite(original) || !Number.isFinite(final) || original !== normal * qty || final !== original - allocated) invalid = true;
    }
    if ((cart?.cart_level_discount_applications || []).some(d => Number(d.total_allocated_amount) > 0 && d.target_type !== 'shipping_line')) invalid = true;
    const validPromotion = mainQuantity > 0 && promotionLines === 1 && applied === reduction && !invalid;
    let state;
    if (cart?.currency !== 'EUR') state = 'currency';
    else if (validPromotion) state = 'success';
    else if (applied > 0 || (lines.length > 0 && invalid)) state = 'conflict';
    else if (lines.length > 0) state = mainQuantity > 0 ? 'existing' : 'regular';
    else state = mainQuantity > 0 ? 'offer' : 'intro';
    return { state, mainQuantity, addonQuantity: lines.reduce((n, i) => n + Number(i.quantity), 0), applied, validPromotion };
  }
  async function request(path, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(base + path, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      if (!response.ok) {
        let detail;
        try { detail = await response.json(); } catch (_) {}
        const error = new Error(typeof detail?.description === 'string' ? detail.description : 'Die Anfrage an den Warenkorb konnte nicht abgeschlossen werden.');
        error.httpStatus = response.status;
        throw error;
      }
      return await response.json();
    } finally { clearTimeout(timeout); }
  }
  function shipping(cart) {
    document.querySelectorAll('[data-cb-cart-shipping]').forEach(el => {
      let text = 'Versandkosten und Lieferland werden im Checkout bestätigt.';
      if (cart.currency === 'EUR' && ['DE', 'AT', 'CH'].includes(cfg.country)) {
        const name = { DE: 'Deutschland', AT: 'Österreich', CH: 'Schweiz' }[cfg.country];
        const fee = cfg.country === 'DE' ? 399 : 499;
        text = cart.total_price >= 3500 ? 'Standardversand nach ' + name + ': kostenlos.' : 'Standardversand nach ' + name + ': ' + format(fee) + ' · ab 35 € kostenlos.';
        if (cfg.country === 'CH') text += ' Einfuhrabgaben können zusätzlich anfallen.';
      } else if (cfg.country === 'CH') text = 'Versand in CHF wird im Checkout berechnet. Einfuhrabgaben können zusätzlich anfallen.';
      el.textContent = text;
    });
  }
  function applyCart(cart) {
    if (!Array.isArray(cart?.items)) return;
    lastCart = cart;
    document.querySelectorAll('cb-addon-offer').forEach(el => el.update(cart));
    shipping(cart);
  }
  async function refresh() {
    const mine = ++revision;
    try { const cart = await request('cart.js'); if (mine === revision) applyCart(cart); return cart; }
    catch (_) {
      document.querySelectorAll('cb-addon-offer').forEach(el => el.networkUnavailable());
      return null;
    }
  }
  function scheduleRefresh() { clearTimeout(timer); timer = setTimeout(refresh, 100); }
  function lock(value) {
    busy = value;
    if (value) {
      document.querySelectorAll('.cart__checkout,.js-qty__adjust,[data-cb-quantity],[data-cb-delta],.add-to-cart,product-form button[type="submit"]').forEach(el => {
        if ('disabled' in el && !el.disabled) { el.disabled = true; heldControls.add(el); }
      });
    } else {
      heldControls.forEach(el => { if (el.isConnected) el.disabled = false; }); heldControls.clear();
    }
    document.querySelectorAll('cb-addon-offer').forEach(el => { el.setAttribute('aria-busy', String(value)); el.buttons(); });
  }
  async function updatePage() {
    const current = document.querySelector('[data-cb-cart-page]');
    if (!current) return;
    const id = current.dataset.cbSection;
    const data = await request('cart?sections=' + encodeURIComponent(id));
    if (typeof data[id] !== 'string') throw new Error('Die Warenkorbansicht konnte nicht aktualisiert werden.');
    const page = new DOMParser().parseFromString(data[id], 'text/html').querySelector('[data-cb-cart-page]');
    if (!page) throw new Error('Die aktualisierte Warenkorbansicht ist nicht verfügbar.');
    current.replaceWith(page);
  }
  async function sync(cart) {
    if (cart) { ++revision; applyCart(cart); }
    document.dispatchEvent(new CustomEvent('cart:build'));
    await updatePage();
    const fresh = await refresh();
    if (!fresh) throw new Error('Bitte prüfe deinen Warenkorb, bevor du fortfährst.');
    return fresh;
  }
  const write = (path, body) => request(path, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
  class AddonOffer extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.addEventListener('change', event => {
        const card = event.target.closest('[data-cb-addon-card]');
        if (card) { this.variantPreview(card); this.buttons(); }
      });
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-cb-addon-add]');
        if (button && this.contains(button)) { event.preventDefault(); this.add(button); }
      });
      this.querySelectorAll('[data-cb-addon-card]').forEach(card => this.variantPreview(card));
      if (lastCart) this.update(lastCart);
      else { this.update({ items: [], item_count: 0, total_price: 0, currency: cfg.currency || 'EUR' }); scheduleRefresh(); }
    }
    list(card) {
      try { const values = JSON.parse(card.querySelector('[data-cb-addon-variants]').textContent); return Array.isArray(values) ? values : []; } catch (_) { return []; }
    }
    selected(card) {
      const choices = Array.from(card.querySelectorAll('[data-cb-addon-option]')).map(el => el.value);
      if (choices.some(v => !v)) return null;
      return this.list(card).find(v => Array.isArray(v.options) && v.options.length === choices.length && v.options.every((value, i) => value === choices[i])) || null;
    }
    variantPreview(card) {
      const selected = this.selected(card);
      const choices = Array.from(card.querySelectorAll('[data-cb-addon-option]')).map(el => el.value);
      const preview = selected || this.list(card).find(v => v.available && v.options?.every((value, i) => !choices[i] || value === choices[i]));
      const image = card.querySelector('img');
      if (image && preview?.featured_image?.src) { image.removeAttribute('srcset'); image.src = preview.featured_image.src; image.alt = card.dataset.productName + ' – ' + preview.title; }
      const out = card.querySelector('[data-cb-addon-variant-note]');
      if (out) out.textContent = selected && !selected.available ? 'Diese Variante ist aktuell nicht verfügbar.' : '';
    }
    buttons() {
      this.querySelectorAll('[data-cb-addon-add]').forEach(button => {
        const variant = this.selected(button.closest('[data-cb-addon-card]'));
        button.disabled = busy || uncertain || cfg.backendReady !== true || this.dataset.state !== 'offer' || !variant?.available || Number(variant.price) !== normal;
      });
    }
    update(cart) {
      const s = evaluateCart(cart);
      this.dataset.state = s.state;
      this._state = s;
      const pdp = this.dataset.surface === 'product';
      this.hidden = s.state === 'intro' && !pdp && cart.item_count === 0;
      const stage = this.querySelector('[data-cb-staging]');
      if (stage) stage.hidden = cfg.backendReady === true;
      const cards = this.querySelector('[data-cb-cards]');
      if (cards) cards.hidden = !['offer', 'intro'].includes(s.state) || (s.state === 'intro' && !pdp);
      const messages = {
        success: 'Aktionspreis angewendet: 9,90 €',
        intro: pdp ? 'Lege zuerst ein Hauptprodukt in den Warenkorb. Danach kannst du dein Extra auswählen.' : 'Ein Hauptprodukt genügt für den Aktionspreis.',
        offer: '',
        regular: 'Kein Hauptprodukt im Warenkorb. Pulau und Bastei werden zum regulären Preis von jeweils 24,00 € berechnet.',
        existing: 'Ein Zusatzprodukt ist bereits im Warenkorb. Der Aktionspreis wurde noch nicht bestätigt. Maßgeblich ist der angezeigte Artikelpreis.',
        conflict: 'Die aktuelle Rabattberechnung weicht von der Zusatzprodukt-Aktion ab. Bitte prüfe die Artikelpreise im Warenkorb.',
        currency: 'Für die CHF-Ansicht ist die Aktionspreisprüfung noch nicht freigegeben. Bitte nutze für diesen Test die EUR-Ansicht.'
      };
      let text = messages[s.state] || '';
      if (s.state === 'success' && s.addonQuantity > 1) text += ' Ein Stück zum Aktionspreis; weitere Pulau und Bastei jeweils 24,00 €.';
      const status = this.querySelector('[data-cb-status]');
      if (status && status.textContent !== text) status.textContent = text;
      this.buttons();
    }
    networkUnavailable() {
      this.dataset.state = 'unavailable'; this.buttons();
      if (this.dataset.surface === 'product') this.hidden = false;
      const status = this.querySelector('[data-cb-status]');
      if (status) status.textContent = 'Der Warenkorb konnte noch nicht geprüft werden. Bitte lade die Ansicht erneut.';
    }
    instance() { return this.isConnected ? this : Array.from(document.querySelectorAll('cb-addon-offer')).find(el => el.dataset.location === this.dataset.location) || this; }
    showError(text) {
      const out = this.instance().querySelector('[data-cb-addon-error]');
      if (out) { out.hidden = false; out.textContent = text; out.focus({ preventScroll: false }); }
    }
    async add(button) {
      if (busy || uncertain || cfg.backendReady !== true) return;
      const card = button.closest('[data-cb-addon-card]'), variant = this.selected(card);
      if (!variant?.available) { this.showError('Bitte wähle eine verfügbare Farbe und gegebenenfalls eine Größe.'); return; }
      if (Number(variant.price) !== normal) { this.showError('Der reguläre Preis hat sich geändert. Bitte prüfe die Produktseite.'); return; }
      const requestId = window.crypto?.randomUUID?.() || Date.now() + '-' + Math.random().toString(36).slice(2);
      const label = button.textContent;
      let attempted = false, added = false, confirmed = false;
      const previousError = this.querySelector('[data-cb-addon-error]'); if (previousError) previousError.hidden = true;
      lock(true); button.textContent = 'Wird hinzugefügt …';
      try {
        const before = await request('cart.js'); ++revision; applyCart(before);
        const s = evaluateCart(before);
        if (s.state !== 'offer') throw new Error(s.mainQuantity === 0 ? 'Bitte lege zuerst eines der neun Hauptprodukte in den Warenkorb.' : 'Ein Zusatzprodukt ist bereits im Warenkorb. Es gilt höchstens ein Aktionspreis pro Bestellung.');
        attempted = true;
        await write('cart/add.js', { items: [{ id: variant.id, quantity: 1, properties: { _cb_offer_request: requestId } }] });
        added = true;
        const after = await request('cart.js');
        const line = cartItems(after).find(i => i.properties?._cb_offer_request === requestId && Number(i.variant_id || i.id) === Number(variant.id));
        if (!line) throw new Error('Die Bestätigung der hinzugefügten Variante steht noch aus.');
        const allocation = (line.line_level_discount_allocations || []).filter(d => d.discount_application?.title === cfg.discountTitle).reduce((n, d) => n + Number(d.amount || 0), 0);
        confirmed = evaluateCart(after).validPromotion && Number(line.quantity) === 1 && Number(line.final_line_price) === offer && allocation === reduction;
        if (!confirmed) {
          // Only remove the isolated request line, never somebody else's existing cart item.
          await write('cart/change.js', { id: line.key, quantity: 0 });
          added = false; attempted = false;
          await sync();
          throw new Error('Der Aktionspreis wurde serverseitig nicht bestätigt. Nur das gerade hinzugefügte Extra wurde wieder entfernt.');
        }
        await sync(after);
        this.instance().querySelector('[data-cb-status]')?.focus({ preventScroll: true });
      } catch (error) {
        if (added || (attempted && (!error.httpStatus || error.httpStatus >= 500))) uncertain = true;
        let text = error.message;
        if (error.name === 'AbortError') text = 'Die Bestätigung dauert länger. Bitte prüfe zuerst deinen Warenkorb. Nicht erneut hinzufügen.';
        if (uncertain && !added) text = 'Die Bestätigung ist unklar. Der Artikel könnte bereits im Warenkorb liegen. Bitte prüfe deinen Warenkorb; nicht erneut hinzufügen.';
        if (added && !confirmed) text = 'Der Artikel könnte bereits im Warenkorb liegen. Bitte prüfe zuerst die Artikelpreise; nicht erneut hinzufügen.';
        if (added && confirmed) text = 'Das Extra wurde zum Aktionspreis hinzugefügt. Bitte öffne den Warenkorb erneut, um die aktualisierte Ansicht zu sehen.';
        await refresh(); this.showError(text);
        if (uncertain) { const review = this.instance().querySelector('[data-cb-addon-review]'); if (review) review.hidden = false; }
      } finally { button.textContent = label; lock(false); }
    }
  }
  if (!customElements.get('cb-addon-offer')) customElements.define('cb-addon-offer', AddonOffer);
  async function quantity(control) {
    if (busy) return;
    const row = control.closest('[data-cb-line]'), input = row?.querySelector('[data-cb-quantity]');
    if (!row || !input) return;
    const delta = control.dataset.cbDelta || '0';
    const qty = delta === 'remove' ? 0 : Number(input.value) + Number(delta);
    const error = text => { const out = document.querySelector('[data-cb-page-error]'); if (out) { out.textContent = text; out.hidden = false; out.focus(); } };
    if (input.value.trim() === '' || !Number.isSafeInteger(qty) || qty < 0) { error('Bitte gib eine gültige ganze Stückzahl ein.'); return; }
    lock(true);
    try { const cart = await write('cart/change.js', { id: row.dataset.cbLine, quantity: qty }); await sync(cart); }
    catch (e) { await refresh(); error(e.message); }
    finally { lock(false); }
  }
  function guardProductForm(event) {
    const form = event.type === 'submit' ? event.target : event.target.closest('button[type="submit"]')?.form;
    if (!(form instanceof HTMLFormElement) || !form.action.includes('/cart/add')) return;
    if (busy) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    const section = form.closest('.product-section') || form.closest('[data-section-type="product"]');
    const check = section?.querySelector('[data-cb-size-confirm]');
    if (check && !check.checked) { event.preventDefault(); event.stopImmediatePropagation(); const error = section.querySelector('[data-cb-size-error]'); if (error) error.hidden = false; check.focus(); }
  }
  document.addEventListener('click', guardProductForm, true);
  document.addEventListener('submit', guardProductForm, true);
  document.addEventListener('click', event => { const control = event.target.closest('[data-cb-delta]'); if (control) { event.preventDefault(); quantity(control); } });
  document.addEventListener('change', event => {
    if (event.target.matches('[data-cb-quantity]')) quantity(event.target);
    if (event.target.matches('[data-cb-size-confirm]') && event.target.checked) { const error = event.target.closest('[data-cb-bastei-check]')?.querySelector('[data-cb-size-error]'); if (error) error.hidden = true; }
  });
  ['cart:updated', 'cart:build', 'ajaxProduct:added', 'shopify:section:load'].forEach(name => document.addEventListener(name, scheduleRefresh));
  window.addEventListener('pageshow', scheduleRefresh);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) scheduleRefresh(); });
  window.CityBorderStore = { version: 'addon-quantity-r2', evaluateCart, refresh };
  scheduleRefresh();
})();
